
import { GenericGrid } from '@amsanalytics/react-shared-library';
import React from 'react';
import styles from './_lease-fees-editor.module.scss';
import { hasValue } from "@amsanalytics/react-shared-library";
import {TileLayout, TileLayoutItem, TilePosition} from "@progress/kendo-react-layout";
import {ILeaseLoanClass} from "../../../../Interfaces/Common/ILeaseLoanClass";
import {IApplication} from "../../../../Interfaces/Application/IApplication";
import {useDialog} from "../../../../providers/DialogProvider";
import GenericItemEditorDialog, {
  IGenericItemEditorError,
  IGenericItemFieldInfo
} from "../../GenericItemEditorDialog/GenericItemEditorDialog";
import {useDispatch, useSelector} from "react-redux";
import produce from "immer";
import {updateEditingApp} from "../../../../store/application/actions";
import {ApplicationDispatch} from "../../../../store/application/types";
import {useBusinessRulesEngine} from "../../../../Data/BusinessRulesData";
import {ILookupItems} from "../../../../Interfaces/ILookupItems";
import {getLeaseFeeByDescription} from "../../../../utils/quoteUtils";
import QuoterError from "../../QuoterError/QuoterError";
import {getLeaseCustomFees} from "../../../../utils/leaseCustomFees";

export interface GenericGridLeaseFee {
  description: string;
  amount: number;
  fieldName: string;
}

export interface LeaseFeesEditorProps {
  selectedLeaseLoanClass: ILeaseLoanClass | undefined;
  leaseQuoteFieldIsDisabled: (fieldName: string) => boolean;
  currentApp: IApplication;
  lookups: ILookupItems;
  errorMessage?: string;
}

export default function LeaseFeesEditor(props: LeaseFeesEditorProps) {
  const applicationLeaseQuote = props.currentApp.applicationLeaseQuote;
  const dispatch = useDispatch<ApplicationDispatch>();
  const businessRulesEngine = useBusinessRulesEngine();
  const [openDialog, closeDialog] = useDialog();
  const leaseFeesForLoanClass = props.lookups.leaseFees.filter(lf => lf.loanClassCode === props.selectedLeaseLoanClass?.code);
  
  const editableFieldInfos: IGenericItemFieldInfo[] = [
    {name: "description", label: "Description", type: "string", required: true, readonly: true},
    {name: "amount", label: "Amount", type: "number", required: true, readonly: false}
  ];

  const handleUpdateFee = (updatedFee: GenericGridLeaseFee): void => {
    const updatedApp = produce(props.currentApp!, updatedCurrentApp => {
      updatedCurrentApp.applicationLeaseQuote[updatedFee.fieldName] = updatedFee.amount;
    })
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
    closeDialog();
  }

  const validateFee = (editedFee: GenericGridLeaseFee): IGenericItemEditorError[] => {
    const errors: IGenericItemEditorError[] = [];

    if (hasValue(editedFee.amount) && editedFee.amount! < 0) {
      errors.push({message: "The amount can't be less than 0", canBeBypassed: false});
    }
    
    const leaseFeeForLoanClass = getLeaseFeeByDescription(leaseFeesForLoanClass, editedFee.description);
    if (leaseFeeForLoanClass) {
      if (hasValue(leaseFeeForLoanClass.min) && editedFee.amount < leaseFeeForLoanClass.min!) {
        errors.push({message: `The amount can't be less than the fee's minimum allowed amount- $${leaseFeeForLoanClass.min}`, canBeBypassed: false});
      }

      if (hasValue(leaseFeeForLoanClass.max) && editedFee.amount > leaseFeeForLoanClass.max!) {
        errors.push({message: `The amount can't be greater than the fee's maximum allowed amount- $${leaseFeeForLoanClass.max}`, canBeBypassed: false});
      }
    }

    return errors;
  }

  const editFee = (fee: GenericGridLeaseFee) => {
    const buttonText = "Update";

    openDialog({
      children: (
        <GenericItemEditorDialog
          item={fee as GenericGridLeaseFee}
          objectSpecificValidator={validateFee}
          title={"Fee"}
          editableFieldInfos={editableFieldInfos}
          submitButtonText={buttonText}
          onSubmit={(updatedFee: GenericGridLeaseFee) => handleUpdateFee(updatedFee)}
          closeDialog={closeDialog}
        />
      )
    })
  }
  
  const convertedFeesToFitGenericGrid: GenericGridLeaseFee[] = [
    {description: "GAP FEE", amount: applicationLeaseQuote.gapFee, fieldName: "gapFee"},
    {description: "DOC FEE", amount: applicationLeaseQuote.docFee, fieldName: "docFee"},
    {description: "TITLE FEE", amount: applicationLeaseQuote.titleFee, fieldName: "titleFee"},
    {description: "LICENSE FEE", amount: applicationLeaseQuote.licenseFee, fieldName: "licenseFee"},
    {description: "BATTERY FEE", amount: applicationLeaseQuote.batteryFee, fieldName: "batteryFee"},
    {description: "TRANSFER FEE", amount: applicationLeaseQuote.transferFee, fieldName: "transferFee"},
    {description: "DELIVERY FEE", amount: applicationLeaseQuote.deliveryFee, fieldName: "deliveryFee"},
    {description: "SERVICE WARRANTY FEE", amount: applicationLeaseQuote.serviceWarranty, fieldName: "serviceWarranty"},
    {description: "DISPOSITION FEE", amount: applicationLeaseQuote.dispositionFee, fieldName: "dispositionFee"},
    {description: "ACQUISITION FEE", amount: applicationLeaseQuote.acquisitionFee, fieldName: "acquisitionFee"},
  ];
  
  getLeaseCustomFees(applicationLeaseQuote, leaseFeesForLoanClass).forEach(({description, fieldName}) => {
    convertedFeesToFitGenericGrid.push({description, amount: applicationLeaseQuote[fieldName] ?? 0, fieldName});
  });

  const [data, setData] = React.useState<Array<TilePosition>>([
    { col: 1, colSpan: 12, rowSpan: 2, row: 1 }
  ]);

  const tiles: TileLayoutItem[] = [
    {
      defaultPosition: data[0],
      reorderable: false,
      resizable: false,
      header: 'Fees',
      body: <div className={styles.leaseFeesEditorContainer}>
        <GenericGrid<GenericGridLeaseFee>
          style={{display: 'flex', flex: 1}}
          columns={[
            {title: "Description", field: "description"},
            {title: "Amount", field: "amount", format: '{0:c}'},
          ]}
          itemName={'Fee'}
          items={convertedFeesToFitGenericGrid.sort((a, b) =>
            a.description > b.description ? 1 : -1
          ) ?? []}
          disableAdding
          disableEditing={props.leaseQuoteFieldIsDisabled("fees")}
          disableExcelExporting={true}
          disablePdfExporting={true}
          disableFiltering={true}
          disableGrouping={true}
          onItemSelected={(fee: GenericGridLeaseFee) => { if (!props.leaseQuoteFieldIsDisabled("fees")) editFee(fee); }}
          disablePaging={convertedFeesToFitGenericGrid.length <= 20}
        />
        <QuoterError message={props.errorMessage}/>
      </div>,
    }
  ];
  
  return (
    <TileLayout
      columns={12}
      className={styles.quoteEditorTileLayout}
      gap={{ rows: 10, columns: 10 }}
      rowHeight={"100%"}
      // style={{display: 'flex', flex: 1}}
      style={{ width: '100%'}}
      positions={data}
      items={tiles}
    />
  );
}
